import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import MessagesPage from "@/pages/MessagesPage";
import AuthCallback from "@/pages/AuthCallback";
import ProfilePage from "@/pages/ProfilePage";
import AdminBI from "@/pages/admin/AdminBI";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ClientDashboard from "@/pages/client/ClientDashboard";
import ClientChart from "@/pages/clinician/ClientChart";
import AdminUserChart from "@/pages/admin/AdminUserChart";
import IntakeWizard from "@/pages/client/IntakeWizard";
import QuestionnaireList from "@/pages/client/QuestionnaireList";
import QuestionnairePage from "@/pages/client/QuestionnairePage";
import BookingPage from "@/pages/client/BookingPage";
import ReportView from "@/pages/client/ReportView";
import ClinicianDashboard from "@/pages/clinician/ClinicianDashboard";
import AvailabilityPage from "@/pages/clinician/AvailabilityPage";
import CaseDetail from "@/pages/clinician/CaseDetail";
import ReportBuilder from "@/pages/clinician/ReportBuilder";
import InterviewPage from "@/pages/clinician/InterviewPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import QaQueue from "@/pages/admin/QaQueue";
import QaReview from "@/pages/admin/QaReview";

function Guard({ roles, children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" data-testid="loading-spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Landing />;
  return <Landing />;
}

function AppRouter({ children }) {
  const location = useLocation();
  // Detect the OAuth session_id in the URL fragment synchronously during render (not in an
  // effect) so the exchange happens before any protected-route auth check — prevents races.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter>
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/messages" element={<Guard><MessagesPage /></Guard>} />
            <Route path="/profile" element={<Guard><ProfilePage /></Guard>} />
            <Route path="/register" element={<Register />} />
            <Route path="/payment/success" element={<Guard roles={["client"]}><PaymentSuccess /></Guard>} />
            <Route path="/payment/cancel" element={<Guard roles={["client"]}><PaymentCancel /></Guard>} />
            <Route path="/app" element={<Guard roles={["client"]}><ClientDashboard /></Guard>} />
            <Route path="/app/case/:caseId/intake" element={<Guard roles={["client"]}><IntakeWizard /></Guard>} />
            <Route path="/app/case/:caseId/questionnaires" element={<Guard roles={["client"]}><QuestionnaireList /></Guard>} />
            <Route path="/app/case/:caseId/questionnaire/:measureId" element={<Guard roles={["client"]}><QuestionnairePage /></Guard>} />
            <Route path="/app/case/:caseId/book" element={<Guard roles={["client"]}><BookingPage /></Guard>} />
            <Route path="/app/case/:caseId/report" element={<Guard roles={["client"]}><ReportView /></Guard>} />
            <Route path="/clinician" element={<Guard roles={["clinician"]}><ClinicianDashboard /></Guard>} />
            <Route path="/clinician/clients/:clientId" element={<Guard roles={["clinician"]}><ClientChart /></Guard>} />
            <Route path="/clinician/availability" element={<Guard roles={["clinician"]}><AvailabilityPage /></Guard>} />
            <Route path="/clinician/cases/:caseId" element={<Guard roles={["clinician"]}><CaseDetail /></Guard>} />
            <Route path="/clinician/cases/:caseId/interview" element={<Guard roles={["clinician"]}><InterviewPage /></Guard>} />
            <Route path="/clinician/cases/:caseId/interview/:interviewId" element={<Guard roles={["clinician"]}><InterviewPage /></Guard>} />
            <Route path="/clinician/cases/:caseId/report" element={<Guard roles={["clinician"]}><ReportBuilder /></Guard>} />
            <Route path="/admin" element={<Guard roles={["admin"]}><AdminDashboard /></Guard>} />
            <Route path="/admin/users/:userId" element={<Guard roles={["admin"]}><AdminUserChart /></Guard>} />
            <Route path="/admin/bi" element={<Guard roles={["admin"]}><AdminBI /></Guard>} />
            <Route path="/admin/qa" element={<Guard roles={["admin"]}><QaQueue /></Guard>} />
            <Route path="/admin/qa/:caseId" element={<Guard roles={["admin"]}><QaReview /></Guard>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AppRouter>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
